<script setup lang="ts">
/**
 * BaseField — components.css 의 .field/.input 패턴 원자.
 *  label(+필수표시) + input + hint + error 메시지.
 *  type=text|email|password — password 는 eye 토글로 표시/숨김 전환.
 *  vee-validate 연동: v-model(modelValue) + error(검증 메시지) props.
 * 색은 토큰 경유 — .field.is-error 시 보더/메시지 danger.
 */
import { computed, ref, useId } from 'vue';
import { Eye, EyeOff } from 'lucide-vue-next';

import {
  FIELD_CONTROL_BASE_CLASS,
  FIELD_CONTROL_ERROR_CLASS,
  FIELD_ERROR_MSG_CLASS,
  FIELD_HINT_MSG_CLASS,
  FIELD_LABEL_CLASS,
} from '#/lib/fieldStyles';
import { cn } from '#/lib/utils';

interface Props {
  /** v-model 값 */
  modelValue?: string;
  /** 라벨 텍스트 */
  label?: string;
  /** 입력 타입 */
  type?: 'text' | 'email' | 'password';
  /** placeholder */
  placeholder?: string;
  /** 필수 표시(*) */
  required?: boolean;
  /** 보조 안내 문구 */
  hint?: string;
  /** 검증 에러 메시지(있으면 is-error) */
  error?: string;
  /** 네이티브 autocomplete */
  autocomplete?: string;
  /** 비활성 */
  disabled?: boolean;
  /** 읽기 전용(값 표시 전용 — 수정 불가). 구글 SSO 이메일처럼 서버 검증값 표시 시 사용. */
  readonly?: boolean;
  /** name 속성(폼 직렬화·vee-validate) */
  name?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  type: 'text',
  placeholder: '',
  required: false,
  hint: '',
  error: '',
  autocomplete: undefined,
  disabled: false,
  readonly: false,
  name: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [event: FocusEvent];
}>();

const fieldId = useId();
const showPassword = ref(false);

const isPassword = computed(() => props.type === 'password');
// password 는 토글 상태에 따라 text/password 전환, 그 외는 props.type 그대로
const inputType = computed(() => {
  if (!isPassword.value) return props.type;
  return showPassword.value ? 'text' : 'password';
});
const hasError = computed(() => props.error.length > 0);

/** 공통 컨트롤 골격 + input padding(+password 우측 여백) + 에러 보더 병합. */
const inputClass = computed(() =>
  cn(
    FIELD_CONTROL_BASE_CLASS,
    'px-[14px] py-3 placeholder:text-muted',
    isPassword.value && 'pr-11',
    hasError.value && FIELD_CONTROL_ERROR_CLASS,
  ),
);

function onInput(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
}
</script>

<template>
  <div class="mb-4" :class="{ 'is-error': hasError }">
    <label v-if="label" :for="fieldId" :class="FIELD_LABEL_CLASS">
      {{ label }}<span v-if="required" class="ml-[3px] text-danger">*</span>
    </label>

    <div class="relative">
      <input
        :id="fieldId"
        :name="name"
        :type="inputType"
        :value="modelValue"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :readonly="readonly || undefined"
        :aria-invalid="hasError || undefined"
        :class="inputClass"
        @input="onInput"
        @blur="$emit('blur', $event)"
      />
      <button
        v-if="isPassword"
        type="button"
        class="absolute right-3 top-1/2 -translate-y-1/2 text-muted transition-colors hover:text-brand"
        :aria-label="showPassword ? '비밀번호 숨기기' : '비밀번호 표시'"
        :aria-pressed="showPassword"
        tabindex="-1"
        @click="showPassword = !showPassword"
      >
        <EyeOff v-if="showPassword" class="h-[18px] w-[18px]" />
        <Eye v-else class="h-[18px] w-[18px]" />
      </button>
    </div>

    <p v-if="hasError" :class="FIELD_ERROR_MSG_CLASS">{{ error }}</p>
    <p v-else-if="hint" :class="FIELD_HINT_MSG_CLASS">{{ hint }}</p>
  </div>
</template>
